import { useMemo, useRef, useState } from "react";
import { Button, Upload, UploadProps } from "antd";
import JSZip from "jszip";
import { saveAs } from "file-saver";

type MaterialColorMap = Map<string, {
  name: string;
  path: string;
  content: string;
  color: number[];
}>;

const SuColour = () => {
  const [materialColorMap, setMaterialColorMap] = useState<MaterialColorMap>();

  const [loading, setLoading] = useState(false);

  const uzip = useRef<JSZip>();

  const materials = useMemo(() => {
    if (!materialColorMap) return [];
    return Array.from(materialColorMap.values());
  }, [materialColorMap]);

  const generateColors = (count: number): number[][] => {
    const colors: Set<string> = new Set();
    const randomColor = () => Math.round(Math.random() * 255);
    while(colors.size < count) {
      colors.add([randomColor(), randomColor(), randomColor()].join(','));
    }
    return Array.from(colors).map(item => item.split(',').map(it => Number(it)));
  };

  const handleBeforeUpload: UploadProps<any>['beforeUpload'] = (file) => {
    try {
      if (!file.name.endsWith('.skp')) throw new Error('请上传SU模型');
      JSZip.loadAsync(file).then(async (zip) => {
        // console.log(zip.files);
        uzip.current = zip;
        const materials = zip.folder('materials');
        if (!materials) throw new Error('请上传SU模型');
        const files = materials.filter((path) => path.endsWith('material.xml'));
        const map: MaterialColorMap = new Map();
        const colors = generateColors(files.length);
        for (const file of files) {
          const [, name] = file.name.split('/');
          const content = await zip.file(file.name)?.async('string');
          if (!content) return;
          const color = colors.pop();
          if (!color) return;
          map.set(file.name, {
            name,
            path: file.name,
            content,
            color,
          });
        }
        setMaterialColorMap(map);
      }).catch((err) => {
        console.log('请传入SU模型');
      });
    } catch(err) {
      console.log(err);
    }
    return false;
  };

  const handleExport = async () => {
    if (!uzip.current || !materialColorMap) return;
    try {
      setLoading(true);
      for (const file of Array.from(materialColorMap.values())) {
        const content = `<?xml version="1.0" encoding="UTF-8" standalone="no" ?>
<materialDocument xmlns="http://sketchup.google.com/schemas/sketchup/1.0/material" xmlns:mat="http://sketchup.google.com/schemas/sketchup/1.0/material">
  <mat:material name="${file.name}" type="0" colorRed="${file.color[0]}" colorGreen="${file.color[1]}" colorBlue="${file.color[2]}" colorizeType="0" trans="1" useTrans="0" hasTexture="0" />
</materialDocument>`;
        uzip.current.file(file.path, content);
      }
      const blob = await uzip.current.generateAsync({ type: 'blob' });
      console.log(blob.size);
      saveAs(blob, 'new.skp');
    } catch(err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div>
        <Upload.Dragger
          accept="application/sketchup"
          beforeUpload={handleBeforeUpload}
          maxCount={1}
        >
          上传SKP模型
        </Upload.Dragger>
      </div>
      <div style={{ marginTop: 20 }}>
        <Button
          block
          disabled={!materialColorMap}
          loading={loading}
          onClick={handleExport}
        >
          导出新模型
        </Button>
      </div>
      <div style={{ marginTop: 20, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        {materials.map(item => (
          <div key={item.path} style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ width: 20, height: 20, backgroundColor: `rgb(${item.color.join(',')})` }} />
            <div style={{ marginLeft: 10 }}>{item.name}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default SuColour;
